// eslint-disable-next-line jira/restricted/react-component-props
import React, { useCallback, useEffect, type ComponentProps } from 'react';
import flow from 'lodash/flow';
import memoizeOne from 'memoize-one';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { type CreateUIAnalyticsEvent, withAnalyticsEvents } from '@atlaskit/analytics-next';
import Placeholder from '@atlaskit/react-ufo/placeholder';
import AnalyticsData from '@atlassian/jira-analytics-web-react/src/components/analytics-data.tsx';
import { AnalyticsSource } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import {
	SCREEN,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import { ANALYTIC_CONTEXTS } from '@atlassian/jira-servicedesk-common/src/utils/analytics/index.tsx';
import type { OrderBy } from '@atlassian/jira-servicedesk-knowledge-services/src/services/rest/articles/index.tsx';
import type {
	Article,
	GenericError,
} from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import KnowledgeHubPushProvider, {
	type PushHistory,
} from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-push-provider/index.tsx';
import { withRouter } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-with-router/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useContainerAri } from '@atlassian/jira-servicedesk-knowledge-common/src/common/use-container/index.tsx';
import type { knowledgeHubBrowseAllArticlesKBTypeCheckerQuery } from '@atlassian/jira-relay/src/__generated__/knowledgeHubBrowseAllArticlesKBTypeCheckerQuery.graphql.ts';
import KnowledgeHubLoadingError from '@atlassian/jira-servicedesk-knowledge-hub/src/view/knowledge-hub-loading-error/index.tsx';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { Redirect } from '@atlassian/react-resource-router';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { browseKnowledge } from '../../state/actions/browse-knowledge/index.tsx';
import { fetchAppLink } from '../../state/actions/fetch-applink/index.tsx';
import { fetchLinkedSpaces } from '../../state/actions/fetch-linked-spaces/index.tsx';
import { getAppLinkOrError } from '../../state/selectors/applink/index.tsx';
import { getArticlesByIds } from '../../state/selectors/articles/index.tsx';
import {
	getProjectId,
	getProjectKey,
	getIsAdmin,
	getActivationId,
} from '../../state/selectors/base/index.tsx';
import {
	getArticleIdsForCategory,
	getCategoryTotalSize,
	getBrowseLimit,
	getBrowseNextCursor,
	getBrowsePrevCursor,
	isBrowseLoading,
	isCategoriesSupported,
} from '../../state/selectors/browse/index.tsx';
import {
	getHubLinkedState,
	getLinkedConfluenceSpace,
	getLinkedConfluenceUrl,
} from '../../state/selectors/hub/index.tsx';
import { getLinkedSpaces } from '../../state/selectors/spaces/index.tsx';
import type { State } from '../../state/types.tsx';
import ReadyForUser from '../common/ready-for-user/index.tsx';
import SearchKnowledgeInline from '../common/search-knowledge-inline/index.tsx';
import { fetchSuggestedTopics } from '../../state/actions/fetch-suggested-topics/index.tsx';
import { triggerArticleGenerationForTopics } from '../../state/actions/trigger-article-generation-for-topics/index.tsx';
import { getTopics } from '../../state/selectors/topics/index.tsx';
import { fetchAIDraftContent } from '../../state/actions/fetch-ai-draft-content/index.tsx';
import { KnowledgeHubCrossSiteArticles } from '../knowledge-hub-cross-site-articles/index.tsx';
import { ThirdPartyNoAccessScreeen } from '../common/thirdparty-no-access-screen/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../common/constants.tsx';
import AllArticlesTable from './all-articles-table/index.tsx';
import parseNumber from './utils/parse-number/index.tsx';
import KnowledgeHubBrowseAllArticles, {
	type KnowledgeHubBrowseArticlesProps,
	type Page,
} from './view.tsx';

type ConnectedProps = {
	page: string | undefined;
	cursor: string;
	sortBy: OrderBy | undefined;
	searchQuery: string;
	spaceKey: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	onCreateArticleEmptyStateClicked: KnowledgeHubBrowseArticlesProps['onCreateArticleEmptyStateClicked'];
	onCreateArticleHeaderClicked: KnowledgeHubBrowseArticlesProps['onCreateArticleHeaderClicked'];
};

type Props = JSX.LibraryManagedAttributes<
	typeof KnowledgeHubBrowseAllArticles,
	ComponentProps<typeof KnowledgeHubBrowseAllArticles>
>;

type ExternalProps = Flow.Diff<
	Props,
	{
		push: PushHistory;
	}
>;
const KnowledgeHubBrowseAllArticlesChecker = (props: ExternalProps) => {
	const cloudId = useCloudId();
	const container = useContainerAri();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const knowledgeBaseSource = useLazyLoadQuery<knowledgeHubBrowseAllArticlesKBTypeCheckerQuery>(
		graphql`
			query knowledgeHubBrowseAllArticlesKBTypeCheckerQuery(
				$cloudId: ID!
				$container: ID!
				$isEnabled: Boolean!
			) {
				knowledgeBase(cloudId: $cloudId) @include(if: $isEnabled) {
					knowledgeBase(container: $container) {
						__typename
						... on KnowledgeBaseSources {
							edge @required(action: THROW) {
								node {
									sourceType @required(action: THROW)
								}
							}
						}
						... on QueryError {
							message
						}
					}
				}
			}
		`,
		{
			cloudId,
			container,
			isEnabled: fg('kb-cross-site-enabled') || fg('3p-integration-enabled_t5ysh'),
		},
	);

	useEffect(() => {
		if (knowledgeBaseSource?.knowledgeBase?.knowledgeBase?.__typename === 'QueryError') {
			fireErrorAnalytics({
				meta: {
					id: 'knowledgeHubArticlesTypeChecker',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				attributes: {
					message: knowledgeBaseSource.knowledgeBase.knowledgeBase.message,
				},
			});
		} else if (
			knowledgeBaseSource?.knowledgeBase?.knowledgeBase?.__typename === 'KnowledgeBaseSources'
		) {
			const sourceType = knowledgeBaseSource.knowledgeBase.knowledgeBase.edge[0]?.node.sourceType;
			fireTrackAnalytics(createAnalyticsEvent({}), 'knowledgeHubArticlesTypeChecker succeeded', {
				sourceType,
			});
		}
	}, [knowledgeBaseSource, createAnalyticsEvent]);

	if (
		knowledgeBaseSource?.knowledgeBase?.knowledgeBase?.__typename === 'QueryError' &&
		(fg('kb-cross-site-enabled') || fg('3p-integration-enabled_t5ysh'))
	) {
		return <KnowledgeHubLoadingError projectKey={props.projectKey} enableHeader={false} />;
	}

	if (
		knowledgeBaseSource?.knowledgeBase?.knowledgeBase?.__typename === 'KnowledgeBaseSources' &&
		(fg('kb-cross-site-enabled') || fg('3p-integration-enabled_t5ysh'))
	) {
		const sourceType = knowledgeBaseSource.knowledgeBase.knowledgeBase.edge[0]?.node.sourceType;
		if (!sourceType)
			return <Redirect to={`/jira/servicedesk/projects/${props.projectKey}/knowledge/setup`} />;
		if (sourceType === 'CROSS_SITE_CONFLUENCE') {
			return (
				<KnowledgeHubPushProvider>
					{({ push }) => <KnowledgeHubCrossSiteArticles {...props} push={push} />}
				</KnowledgeHubPushProvider>
			);
		}
		if (['SHAREPOINT', 'GOOGLE_DRIVE'].includes(sourceType)) {
			return <ThirdPartyNoAccessScreeen sourceType={sourceType} />;
		}
	}

	return (
		<KnowledgeHubPushProvider>
			{({ push }) => <KnowledgeHubBrowseAllArticles {...props} push={push} />}
		</KnowledgeHubPushProvider>
	);
};

const KnowledgeHubBrowseAllArticlesWithPush = (props: ExternalProps) => {
	const renderer = useCallback(() => {
		return <KnowledgeHubLoadingError projectKey={props.projectKey} enableHeader={false} />;
	}, [props.projectKey]);
	const onError = () => {
		fireErrorAnalytics({
			meta: {
				id: 'knowledgeHubArticlesTypeCheckerQuery',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
		});
	};

	return (
		<ErrorBoundary
			id="knowledgeHubBrowseAllArticles"
			packageName="jira-servicedesk-knowledge-hub"
			render={renderer}
			onError={onError}
		>
			<Placeholder name="knowledgeHubBrowseAllArticles" fallback={<Spinner />}>
				<KnowledgeHubBrowseAllArticlesChecker {...props} />
			</Placeholder>
		</ErrorBoundary>
	);
};
const withRouterPush = withRouter(KnowledgeHubBrowseAllArticlesWithPush);
const mapStateToPropsFactory = () => {
	const spaceKeysFactory = memoizeOne((spaceKey: string) => (spaceKey ? JSON.parse(spaceKey) : {}));
	const articlesPageFactory = memoizeOne(
		(state: State, articleIds: string[] | GenericError | undefined) => ({
			totalSize: getCategoryTotalSize(state, 'all-articles'),
			// @ts-expect-error - TS2322 - Type 'Article | GenericError' is not assignable to type 'Article[]'. | TS2345 - Argument of type 'string[] | GenericError' is not assignable to parameter of type 'string[]'.
			articles: getArticlesByIds(state, articleIds).reduce(
				/* eslint-disable-next-line jira/js/no-reduce-accumulator-spread */ // @ts-expect-error - TS2769 - No overload matches this call. | TS2339 - Property 'error' does not exist on type 'Article | GenericError'.
				(acc, curr) => (curr.error ? acc : [...acc, curr]),
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				[] as Article[],
			),
		}),
	);
	return (state: State, ownProps: ConnectedProps) => {
		const page = parseNumber(ownProps.page, 1);
		const { cursor } = ownProps;
		const sortBy = ownProps.sortBy ?? '-lastModified';
		const searchQuery = ownProps.searchQuery ? ownProps.searchQuery : '';
		const spaceKeys = spaceKeysFactory(ownProps.spaceKey);
		const articleIds = getArticleIdsForCategory(state, 'all-articles', page);
		const activationId = getActivationId(state);
		let error: GenericError | undefined;
		let articlesPage: Page | undefined;

		if (!articleIds) {
			articlesPage = undefined;
			// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'string[] | GenericError'.
		} else if (articleIds.error && !Array.isArray(articleIds)) {
			error = articleIds;
		} else {
			// @ts-expect-error - TS2322 - Type 'Article' is not assignable to type 'Article[]'.
			articlesPage = articlesPageFactory(state, articleIds);
		}

		return {
			appLinkOrError: getAppLinkOrError(state),
			linkedState: getHubLinkedState(state),
			topics: getTopics(state),
			spaceKey: getLinkedConfluenceSpace(state),
			confluenceUrl: getLinkedConfluenceUrl(state),
			limit: getBrowseLimit(state),
			isLoading: isBrowseLoading(state, 'all-articles'),
			categoriesSupported: isCategoriesSupported(state, 'all-articles'),
			page,
			sortBy,
			error,
			articlesPage,
			ReadyForUser,
			activationId,
			SearchKnowledgeInline,
			ArticlesTable: AllArticlesTable,
			projectId: getProjectId(state),
			projectKey: getProjectKey(state),
			linkedSpaces: getLinkedSpaces(state),
			cursor,
			searchQuery,
			spaceKeys,
			prevCursor: getBrowsePrevCursor(state, 'all-articles'),
			nextCursor: getBrowseNextCursor(state, 'all-articles'),
			isAdmin: getIsAdmin(state),
		};
	};
};

export default flow([
	AnalyticsSource('knowledgeBrowseAllArticles', SCREEN),
	AnalyticsData({
		attributes: {
			context: ANALYTIC_CONTEXTS.KNOWLEDGE,
		},
	}),
	withAnalyticsEvents(),
	withFireUiAnalytics({
		onCreateArticleHeaderClicked: 'createArticleHeaderLink',
		onCreateArticleEmptyStateClicked: 'createArticleEmptyStateLink',
	}),
	connect(mapStateToPropsFactory, {
		onBrowseArticles: browseKnowledge,
		getSuggestedTopics: fetchSuggestedTopics,
		onTriggerArticleGenerationForTopics: triggerArticleGenerationForTopics,
		getAiDraftPreviewContent: fetchAIDraftContent,
		getLinkedSpaces: fetchLinkedSpaces,
		onFetchAppLink: fetchAppLink,
	}),
])(withRouterPush);
